/**
 * Sprint completion — pure, deterministic roll-ups for the "Complete Sprint"
 * summary and for the Learn-phase historical record captured when a single task
 * is completed.
 *
 *   • {@link buildSprintCompletion} — the end-of-sprint scoreboard: committed /
 *     completed / carryover effort, tasks completed X/Y, blocked tasks, goals
 *     advanced, and estimate accuracy (delegating to the aggregation engine).
 *   • {@link buildHistoricalDraft}  — projects a just-completed task (+ its saved
 *     estimate) into a {@link HistoricalEstimateDraft} so the Learn phase can
 *     silently record estimate-vs-actual for future AI signal.
 *
 * Pure TS — no React, no I/O.
 */

import type { Task } from '../../tasks/types';
import type { TaskEstimate } from '../estimate/types';
import type {
  HistoricalEstimate,
  HistoricalEstimateDraft,
} from '../models/types';
import { estimateAccuracy, type EstimateAccuracy } from '../engine/aggregation';

/** The end-of-sprint summary scoreboard. */
export interface SprintCompletionSummary {
  /** Total committed effort in ideal days. */
  committedEffortDays: number;
  /** Completed (DONE) effort in ideal days. */
  completedEffortDays: number;
  /** Carryover (not-DONE) effort in ideal days. */
  carryoverEffortDays: number;
  /** Number of DONE tasks. */
  tasksCompleted: number;
  /** Total number of tasks in the sprint. */
  tasksTotal: number;
  /** Count of tasks still blocked at completion (not DONE). */
  blockedTasks: number;
  /** Number of distinct goals advanced by the sprint's DONE tasks. */
  goalsAdvanced: number;
  /** Estimate accuracy across the sprint's captured historical records. */
  accuracy: EstimateAccuracy;
  /** The not-DONE tasks that will carry over. */
  carryoverTasks: Task[];
}

/** Rounds to 2 decimals. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function taskDays(task: Task): number {
  const d = task.effortDays;
  return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : 0;
}

function isDone(task: Task): boolean {
  return task.boardStatus === 'DONE';
}

/**
 * Builds the sprint completion summary from the sprint's tasks and the historical
 * records captured for those tasks (used only for the accuracy figure).
 */
export function buildSprintCompletion(
  tasks: Task[],
  historicalRecords: Pick<
    HistoricalEstimate,
    'finalEstimateDays' | 'actualEffortDays'
  >[],
): SprintCompletionSummary {
  const list = tasks ?? [];
  const done = list.filter(isDone);
  const carryoverTasks = list.filter((t) => !isDone(t));

  const committed = round2(list.reduce((s, t) => s + taskDays(t), 0));
  const completed = round2(done.reduce((s, t) => s + taskDays(t), 0));
  const carryover = round2(carryoverTasks.reduce((s, t) => s + taskDays(t), 0));

  const goalsAdvanced = new Set(
    done.map((t) => t.goalId).filter((g): g is string => !!g),
  ).size;

  const blockedTasks = carryoverTasks.filter((t) => t.blocked).length;

  return {
    committedEffortDays: committed,
    completedEffortDays: completed,
    carryoverEffortDays: carryover,
    tasksCompleted: done.length,
    tasksTotal: list.length,
    blockedTasks,
    goalsAdvanced,
    accuracy: estimateAccuracy(historicalRecords ?? []),
    carryoverTasks,
  };
}

/**
 * Projects a just-completed task (+ its saved estimate, when present) and the
 * captured actual effort into a historical-estimate draft. The estimate — when
 * available — supplies the ORIGINAL estimate, the complexity factors, and the
 * extra-work items; the task's mirrored numbers are the fallback and the source
 * of the FINAL (committed) estimate + confidence + service.
 */
export function buildHistoricalDraft(
  task: Task,
  estimate: TaskEstimate | null | undefined,
  actualEffortDays: number,
): HistoricalEstimateDraft {
  const taskEffort =
    typeof task.effortDays === 'number' && task.effortDays > 0
      ? task.effortDays
      : 0;
  const estEffort =
    estimate && typeof estimate.effortDays === 'number' && estimate.effortDays > 0
      ? estimate.effortDays
      : 0;

  const original = estEffort || taskEffort;
  const final = taskEffort || estEffort;
  const confidence =
    task.confidence ?? estimate?.confidence ?? 0;

  return {
    taskId: task.id,
    taskType: task.type ?? 'Task',
    originalEstimateDays: round2(original),
    finalEstimateDays: round2(final),
    actualEffortDays: round2(Math.max(0, actualEffortDays)),
    confidence,
    serviceId: task.serviceId,
    complexityFactors: estimate?.complexity ?? [],
    extraWork: estimate?.extraWork ?? [],
  };
}
